import React, { useEffect, useRef, useState } from "react";
import { Heat } from "../common/model/Heat";
import { EventSessionUser, EventUser } from "../common/model/User";
import { LycraColorConfig, DEFAULT_LYCRA_COLORS } from "../common/model/LycraColorConfig";
import styles from "./heat.module.css";
import { ChromePicker } from "react-color";
import { Button, Input } from "@/components/ui";
import moment from "moment";

type HeatDetailDialogProps = {
  eventId: number;
  doCreate: boolean;
  heat: Heat;
  onSave: (heat: Heat) => void;
  showDialog: (visible: boolean) => void;
  availableUsersInput: EventUser[];
  headerText: string;
  availableColors?: LycraColorConfig[];
};

function HeatDetail({
  eventId,
  doCreate,
  heat,
  onSave,
  showDialog,
  headerText,
  availableUsersInput,
  availableColors = DEFAULT_LYCRA_COLORS,
}: HeatDetailDialogProps) {
  // Use the first color from available colors as default, or fall back to black
  const defaultColor = availableColors.length > 0 ? availableColors[0].color : "#000000";

  const [newHeatName, setNewHeatName] = useState(doCreate ? "" : heat.name);
  const [newHeatNumber, setNewHeatNumber] = useState(
    doCreate ? 0 : heat.heatnr
  );
  const [newHeatLocalId, setNewHeatLocalId] = useState(doCreate ? "" : (heat.localid || ""));
  const [newHeatStartDateTime, setNewHeatStartDateTime] = useState(
    doCreate ? "" : (heat.startdatetime ? moment(heat.startdatetime).format('YYYY-MM-DDTHH:mm') : "")
  );
  const [newHeatEndDateTime, setNewHeatEndDateTime] = useState(
    doCreate ? "" : (heat.enddatetime ? moment(heat.enddatetime).format('YYYY-MM-DDTHH:mm') : "")
  );
  const [newHeatUsers, setNewHeatUsers] = useState<EventSessionUser[]>(
    heat.eventSessionUsers
  );
  const [availableUsers, setAvailableUsers] = useState<EventUser[]>(
    getFilteredUsers(availableUsersInput, heat.eventSessionUsers)
  );
  const selectUserRef = useRef<HTMLSelectElement>(null);
  const selectUserDeviceRef = useRef<HTMLSelectElement>(null);
  const selectColorRef = useRef<HTMLSelectElement>(null);
  const [selectedColor, setSelectedColor] = useState(defaultColor); // State for the picked color
  const [showColorPicker, setShowColorPicker] = useState(false); // State to toggle color picker visibility

  useEffect(() => {
    setNewHeatUsers(heat.eventSessionUsers);
    setAvailableUsers(
      getFilteredUsers(availableUsersInput, heat.eventSessionUsers)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getFilteredUsers(
    availableUsersInput: EventUser[],
    eventSessionUsers: EventSessionUser[]
  ) {
    console.log("availableUsersInput: " + availableUsersInput);
    console.log("eventSessionUsers: " + eventSessionUsers);

    if (eventSessionUsers) {
      return availableUsersInput.filter(
        (eventUser) =>
          eventSessionUsers.find(
            (user) => eventUser.id === user.eventuser.id
          ) === undefined
      );
    }
    return availableUsersInput;
  }
  // function toEventSessionUsers(newHeatUsers: EventUser[]) {
  //   const eventsessionUsers: EventSessionUser[] = newHeatUsers.map((user) => ({
  //     eventuser: user,
  //     id: 0,
  //     eventsessionid: heat.id,
  //     eventuseridsource: undefined,
  //     lycracolor: "#FFFFFF",
  //   }));
  //   return eventsessionUsers;
  // }

  function toEventSessionUser(
    newHeatUser: EventUser,
    color: string,
    newHeatUsersDevice?: EventUser
  ) {
    const eventsessionUsers: EventSessionUser = {
      eventuser: newHeatUser,
      id: 0,
      eventsessionid: heat.id,
      eventuseridsource: newHeatUsersDevice
        ? newHeatUsersDevice.id
        : newHeatUser.id,
      lycracolor: color,
    };
    return eventsessionUsers;
  }

  // Helper function to get device ping status as text
  function getDevicePingStatus(lastping: Date | null | undefined | any): string {
    if (!lastping) {
      return 'Offline';
    }

    const now = moment();
    const lastpingStr = String(lastping);
    let lastPingMoment: moment.Moment;

    if (typeof lastping === 'string') {
      lastPingMoment = moment.utc(lastpingStr.replace("[UTC]", "")).local();
    } else if (typeof lastping === 'number') {
      lastPingMoment = moment(lastping);
    } else {
      lastPingMoment = moment.utc(lastpingStr.replace("[UTC]", "")).local();
    }

    if (!lastPingMoment.isValid()) {
      return 'Invalid';
    }

    const minutesAgo = now.diff(lastPingMoment, 'minutes');
    const secondsAgo = now.diff(lastPingMoment, 'seconds');

    if (minutesAgo < 1) {
      return `${secondsAgo} sec ago`;
    } else {
      return `${minutesAgo} min ago`;
    }
  }

  // function toEventUsers(sessionUsers: EventSessionUser[]) {
  //   const eventUsers: EventUser[] = sessionUsers.map((user) => user.eventuser);
  //   return eventUsers;
  // }

  const handleAddHeat = () => {
    // For backend compatibility, we need to clean the user object to avoid deserialization issues
    // The backend expects only the eventuser ID reference, not the full nested user object
    const cleanedEventSessionUsers = newHeatUsers.map(sessionUser => {
      // Create a minimal eventuser that matches what the backend expects
      const cleanedEventUser: any = {
        id: sessionUser.eventuser.id,
        displayname: sessionUser.eventuser.displayname,
        localid: sessionUser.eventuser.localid,
        issource: sessionUser.eventuser.issource,
        lastping: sessionUser.eventuser.lastping,
        profilepicid: sessionUser.eventuser.profilepicid,
        countryFlag: sessionUser.eventuser.countryFlag
      };
      
      // Only add minimal user info if needed
      if (sessionUser.eventuser.user) {
        cleanedEventUser.userId = sessionUser.eventuser.user.id;
      }

      return {
        id: sessionUser.id,
        eventuser: cleanedEventUser,
        eventsessionid: sessionUser.eventsessionid,
        eventuseridsource: sessionUser.eventuseridsource,
        lycracolor: sessionUser.lycracolor,
        // Include any existing heat-specific data
        lastheight: sessionUser.lastheight,
        lastairtime: sessionUser.lastairtime,
        lastdistance: sessionUser.lastdistance,
        maxheight: sessionUser.maxheight,
        maxairtime: sessionUser.maxairtime,
        maxdistance: sessionUser.maxdistance,
        lastjumpepoch: sessionUser.lastjumpepoch,
        lastspeed: sessionUser.lastspeed,
        maxspeed: sessionUser.maxspeed,
        currentheartrate: sessionUser.currentheartrate,
        currentspeed: sessionUser.currentspeed
      };
    });

    const newHeat: any = {
      id: doCreate ? 0 : heat.id,
      eventid: heat.eventid,
      name: newHeatName,
      heatnr: newHeatNumber,
      localid: newHeatLocalId || null,
      startdatetime: newHeatStartDateTime ? moment(newHeatStartDateTime).toISOString() : null,
      enddatetime: newHeatEndDateTime ? moment(newHeatEndDateTime).toISOString() : null,
      active: false, // New heats are inactive by default
      eventSessionUsers: cleanedEventSessionUsers,
    };
    
    // SEND NEW HEAT TO THE SERVER
    onSave(newHeat);
    console.log(newHeat);
    showDialog(false);

    // Reset state values for next heat addition
    setNewHeatName("");
    setNewHeatNumber(0);
    setNewHeatLocalId("");
    setNewHeatStartDateTime("");
    setNewHeatEndDateTime("");
    setNewHeatUsers([]);
  };

  const addUserToHeat = (
    user: EventUser,
    color: string,
    userDevice?: EventUser
  ) => {
    setNewHeatUsers((prevUsers) => [
      ...prevUsers,
      toEventSessionUser(user, color, userDevice),
    ]);
    setAvailableUsers((prevAvailable) =>
      prevAvailable.filter((u) => u.id !== user.id)
    );
  };

  const removeUserFromHeat = (userId: number) => {
    const removedUserList = newHeatUsers.filter(
      (u) => u.eventuser.id === userId
    );
    setNewHeatUsers((prevUsers) =>
      prevUsers.filter((u) => u.eventuser.id !== userId)
    );
    removedUserList.forEach((removedUser) =>
      setAvailableUsers([...availableUsers, removedUser.eventuser])
    );
  };

  function getDisplayNameForEventUser(eventuseridsource: number) {
    return availableUsersInput.find((user) => user.id === eventuseridsource);
  }

  return (
    <div className={styles.modal}>
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css"
      />
      <div className={styles.modalContent}>
        <h2 className="mb-2">{headerText}</h2>

        {/* Compact inline form fields */}
        <div className="space-y-0.5 mb-3">
          <div className="flex items-center gap-3">
            <label className="w-36 text-sm font-medium text-gray-700">Heat Number:</label>
            <Input
              type="number"
              value={newHeatNumber}
              onChange={(e) => setNewHeatNumber(Number(e.target.value))}
              className="flex-1"
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="w-36 text-sm font-medium text-gray-700">Heat Name:</label>
            <Input
              type="text"
              value={newHeatName}
              onChange={(e) => setNewHeatName(e.target.value)}
              className="flex-1"
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="w-36 text-sm font-medium text-gray-700">External Heat ID:</label>
            <Input
              type="text"
              value={newHeatLocalId}
              onChange={(e) => setNewHeatLocalId(e.target.value)}
              placeholder="e.g., 10"
              className="flex-1"
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="w-36 text-sm font-medium text-gray-700">Start Time (UTC):</label>
            <Input
              type="datetime-local"
              value={newHeatStartDateTime}
              onChange={(e) => setNewHeatStartDateTime(e.target.value)}
              className="flex-1"
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="w-36 text-sm font-medium text-gray-700">End Time (UTC):</label>
            <Input
              type="datetime-local"
              value={newHeatEndDateTime}
              onChange={(e) => setNewHeatEndDateTime(e.target.value)}
              min={newHeatStartDateTime}
              className="flex-1"
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <label>Users:</label>
          <select ref={selectUserRef}>
            <option value="" disabled>
              Select a user...
            </option>
            {availableUsers
              .filter((user) => !user.issource)
              .sort((a, b) => {
                if (a.displayname != null && b.displayname) {
                  return a.displayname.localeCompare(b.displayname);
                } else {
                  return a.user.name!.localeCompare(b.user.name!);
                }
              })
              .map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayname}
                </option>
              ))}
          </select>
        </div>
        <div className={styles.formRow}>
          <label>
            Rider Lycra Color:
            <div className={styles.colorPickerContainer}>
              {" "}
              {/* Add a container for styling */}
              <div
                className={styles.colorPreview}
                onClick={() => setShowColorPicker(!showColorPicker)}
                style={{ backgroundColor: selectedColor }}
              ></div>
              {showColorPicker && (
                <div className={styles.colorPickerOverlay}>
                  <div className={styles.colorPickerWrapper}>
                    <ChromePicker
                      color={selectedColor}
                      onChangeComplete={(color) => setSelectedColor(color.hex)}
                    />
                    <Button onClick={() => setShowColorPicker(false)}>
                      OK
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </label>
          <select
            ref={selectColorRef}
            value={selectedColor}
            onChange={(e) => setSelectedColor(e.target.value)}
          >
            {availableColors
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((colorConfig) => (
                <option key={colorConfig.color} value={colorConfig.color}>
                  {colorConfig.name}
                </option>
              ))}
          </select>
        </div>
        <div className={styles.formRow}>
          <label>
            <div className={styles.infoText}>
              Optional: <br /> User Account of Device which the upper user is
              using for the Heat:
            </div>
          </label>
          <select ref={selectUserDeviceRef}>
            <option value="-1">Select User logged in on Device...</option>
            {availableUsersInput
              .filter((user) => user.issource)
              .sort((a, b) => {
                const aName = a.displayname || a.user.name || '';
                const bName = b.displayname || b.user.name || '';

                // Natural sort for device names with numbers
                return aName.localeCompare(bName, undefined, {
                  numeric: true,
                  sensitivity: 'base'
                });
              })
              .map((user) => {
                const status = getDevicePingStatus(user.lastping);
                return (
                  <option key={user.id} value={user.id}>
                    {user.displayname} ({status})
                  </option>
                );
              })}
          </select>
        </div>
        <Button
          onClick={() => {
            if (selectUserRef.current) {
              let added = false;
              let selectedUser = Number(selectUserRef.current.value);
              let selectedColor = "" + selectColorRef.current?.value;
              const userToAdd = availableUsers.find(
                (u) => u.id === selectedUser
              );
              if (selectUserDeviceRef.current) {
                let selectedUserDevice = Number(
                  selectUserDeviceRef.current.value
                );
                const userDeviceToAdd = availableUsersInput.find(
                  (u) => u.id === selectedUserDevice
                );
                if (userToAdd && userDeviceToAdd) {
                  addUserToHeat(userToAdd, selectedColor, userDeviceToAdd);
                  added = true;
                }
              }
              if (!added && userToAdd) {
                addUserToHeat(userToAdd, selectedColor);
              }
            }
          }}
        >
          Add User to Heat
        </Button>
        <ul>
          {newHeatUsers.map((eventSessionUser) => (
            <li key={eventSessionUser.id} className={styles.userItemModal}>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#">
                {" "}
                <i
                  className="fas fa-trash"
                  style={{ fontSize: 20, color: "#f44336" }}
                  onClick={() =>
                    removeUserFromHeat(eventSessionUser.eventuser.id)
                  }
                ></i>
              </a>
              <span
                className={styles.colorSpan}
                style={{
                  backgroundColor: eventSessionUser.lycracolor || "#000000",
                }}
              ></span>
              <span>{eventSessionUser.eventuser.displayname}</span>
              {eventSessionUser.eventuseridsource &&
                eventSessionUser.eventuseridsource !==
                  eventSessionUser.eventuser.id && (
                  <span className={styles.infoText}>
                    (On:{" "}
                    {
                      getDisplayNameForEventUser(
                        eventSessionUser.eventuseridsource
                      )?.displayname
                    }
                    )
                  </span>
                )}
            </li>
          ))}
        </ul>
        <div className={styles.buttonContainer}>
          <Button
            className="!bg-green-600 !hover:bg-green-700"
            onClick={handleAddHeat}
          >
            Save Heat
          </Button>
          <Button variant="delete" onClick={() => showDialog(false)}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

export default HeatDetail;
